import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Heart,
  Target,
  Users,
  Award,
  Shield,
  Monitor,
  Briefcase,
  Mail,
  Code2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import profileImage from "@/assets/images/teamMembers/mike-profile-image.png";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const values = [
  {
    icon: Target,
    title: "Results-Driven",
    description:
      "Success means a shipped product — signed desktop releases, working cloud backends, and founders who can put their app in front of real users.",
  },
  {
    icon: Heart,
    title: "Partnership Mindset",
    description:
      "Every engagement is treated like my own product — invested in your long-term success, not just the current milestone.",
  },
  {
    icon: Users,
    title: "Transparent Communication",
    description:
      "No surprises. Open, honest dialogue throughout every build — with full visibility through the client portal.",
  },
  {
    icon: Award,
    title: "Excellence",
    description:
      "High standards in architecture, security, desktop packaging, and delivery — the same rigor I brought to enterprise systems.",
  },
];

const credentials = [
  { value: "9+", label: "Years Experience", icon: Briefcase },
  { value: "AWS", label: "Certified", icon: Code2 },
  { value: "CompTIA", label: "Security+", icon: Shield },
  { value: "E2E", label: "Full-Stack Studio", icon: Monitor },
];

const techStack = {
  "Desktop & Client": [
    "Electron",
    "Tauri",
    "React",
    "TypeScript",
    "Code Signing",
    "Auto-Update",
    "macOS & Windows",
  ],
  "Platform & Cloud": [
    "AWS CDK",
    "Lambda",
    "API Gateway",
    "DynamoDB",
    "S3",
    "Cognito",
    "Serverless",
  ],
  "AI & Integration": [
    "LLM Integration",
    "RAG Pipelines",
    "Local Inference",
    "Model Orchestration",
    "Prompt Engineering",
    "AI Evaluations",
  ],
  "Security & Delivery": [
    "IAM Policy Design",
    "Secrets Management",
    "CI/CD Pipelines",
    "Infrastructure as Code",
    "Automated Testing",
    "Release Engineering",
  ],
};

export default function AboutPage() {
  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <div className="flex flex-col items-center mb-8">
              <Badge variant="info" className="mb-4">
                About Echopeak Labs
              </Badge>
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Your Desktop Idea,{" "}
              <span className="text-gradient">Built End to End</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Echopeak Labs is a solo studio for founders who want to turn a
              desktop app idea — especially AI-powered — into a real product.
              I build the platform and the native client, delivered directly
              with full transparency.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" variant="outline" asChild>
                <Link to="/services">
                  Explore What We Do
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {credentials.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                <Card className="glass-card border-gradient text-center">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <stat.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="font-display text-3xl font-bold text-primary mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat.label}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-card/50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge variant="info" className="mb-4">
              Our Mission
            </Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              Built to Ship{" "}
              <span className="text-gradient">Desktop Products</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="prose prose-invert prose-lg">
              <p className="text-muted-foreground leading-relaxed mb-6">
                Most founders with a desktop app idea get stuck between
                no-code prototypes that cannot scale and agencies that split
                work across teams who have never shipped a native client. The
                gap between "I have a vision" and "users can download my app"
                is where projects die.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Echopeak Labs exists to close that gap. One senior engineer
                builds and runs the full stack for you — discovery,
                architecture, cloud platform, desktop client, code signing,
                and release ops — inside your own AWS account and
                repositories, so you get a cohesive product, not a patchwork
                of handoffs.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Everything is built directly in infrastructure you own and
                control. You grant scoped, revocable access keys for the
                engagement; you keep 100% of the code, the cloud account, and
                the intellectual property, from the first commit.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Whether you are starting from a name and a napkin sketch or
                rescuing a half-built MVP, the goal is the same: a shippable
                AI-powered desktop app with the infrastructure to support it.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge variant="info" className="mb-4">
              Meet the Founder
            </Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              Enterprise <span className="text-gradient">Engineering</span>{" "}
              Background
            </h2>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="glass-card border-gradient">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-24 h-24 rounded-full border-4 border-primary/20 overflow-hidden mb-3">
                        <img
                          src={profileImage}
                          alt="Mike — Founder of Echopeak Labs"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-display text-lg font-bold">Mike</h3>
                      <p className="text-sm text-primary">Founder & CTO</p>
                    </div>
                    <div className="flex-1">
                      <div className="prose prose-invert prose-lg max-w-none">
                        <p className="text-muted-foreground leading-relaxed mb-4">
                          I spent 8+ years as a Senior Software Engineer at
                          CyberSmart Ltd, building cloud infrastructure and
                          security tooling that protected thousands of
                          businesses. That experience — shipping production
                          systems at scale — is what I bring to every desktop
                          app engagement.
                        </p>
                        <p className="text-muted-foreground leading-relaxed">
                          I founded Echopeak Labs in 2025 to work directly
                          with founders who need a senior engineer to build
                          and run their entire product stack, inside their
                          own infrastructure. Every engagement gets my full
                          attention, from architecture through signed
                          release.
                        </p>
                      </div>
                      <ul className="space-y-2 text-muted-foreground mt-4">
                        <li className="flex items-start gap-2">
                          <Award className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>
                            Built and shipped cloud platforms (ECS, API Gateway,
                            Lambda) for enterprise-scale products
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Award className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>
                            Designed CI/CD pipelines and infrastructure
                            automation for security-focused delivery
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Award className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>
                            Now applying that rigor to end-to-end desktop app
                            delivery for founders
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="font-display text-xl font-bold mb-4 text-center">
                Certifications
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="glass-card border-gradient">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Code2 className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold text-lg mb-2">
                      AWS Certified
                    </h4>
                    <p className="text-primary font-medium mb-1">
                      Developer Associate
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Amazon Web Services
                    </p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-gradient">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Shield className="h-8 w-8 text-primary" />
                    </div>
                    <h4 className="font-semibold text-lg mb-2">
                      CompTIA Certified
                    </h4>
                    <p className="text-primary font-medium mb-1">Security+</p>
                    <p className="text-sm text-muted-foreground">
                      Computing Technology Industry Association
                    </p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-display text-xl font-bold mb-4 text-center">
                Technical Expertise
              </h3>
              <Card className="glass-card border-gradient">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {Object.entries(techStack).map(
                      ([category, technologies]) => (
                        <div key={category}>
                          <h4 className="font-semibold text-sm text-primary mb-3">
                            {category}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {technologies.map((tech) => (
                              <Badge
                                key={tech}
                                variant="secondary"
                                className="text-xs"
                              >
                                {tech}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-card/50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge variant="info" className="mb-4">
              The Studio
            </Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              A <span className="text-gradient">Different</span> Approach
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <Card className="glass-card border-gradient">
              <CardContent className="p-8">
                <div className="prose prose-invert prose-lg max-w-none">
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Echopeak Labs is a focused solo studio built for one thing:
                    helping founders ship AI-powered desktop apps. You get
                    senior-level full-stack delivery without the overhead of a
                    large agency or the risk of junior handoffs.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6 my-8">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">
                          Target Clients
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Founders with an idea and some things already in
                          place (like marketing) but no technical team, and
                          innovators with just a business name and a desktop
                          app idea — especially AI-powered
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Monitor className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">
                          Core Focus
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          End-to-end desktop product delivery — native client,
                          cloud platform, infrastructure, and release ops
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Target className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">
                          Based in Canada
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Operating with flexible hours to collaborate with
                          clients across time zones
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Award className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">
                          Senior-Level Delivery
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Every engagement is led by experienced engineers — no
                          handoffs to junior staff or outsourced teams
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    Whether you need a blueprint, an MVP rescue, a full AI
                    desktop launch, or ongoing release engineering — I bring
                    the same rigor forged in enterprise systems directly to
                    your product.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge variant="info" className="mb-4">
              How We Work
            </Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold">
              Our <span className="text-gradient">Values</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full glass-card border-gradient">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-card/50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="info" className="mb-4">
              Let's Work Together
            </Badge>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
              Ready to Build Your{" "}
              <span className="text-gradient">Desktop App</span>?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Whether you have a napkin sketch or a half-built prototype, let's
              talk about turning your idea into a shippable product.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to="/intake">
                  <Mail className="mr-2 h-5 w-5" />
                  Book a Discovery Call
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
